import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { IssueLiabilityDoc, IssueLiabilityHistoryEntry } from '@gooduncles/gu-app-schema';
import { Button, DatePicker, Input, Popconfirm, Space, Table, Tag, Tooltip, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useCallback, useMemo, useState } from 'react';

import { formatDate } from 'src/utils/date-util';
import { updateIssueLiability } from 'src/utils/firebase/firebase-short-cut';

import useSWRUsers from 'src/hooks/swr/useSWRUsers';
import { formatDisplayName } from 'src/hooks/subscription/useStaffUsers';

interface LiabilityHistoryManagerProps {
  liability: IssueLiabilityDoc;
  currentUserId: string;
}

/** 인라인 편집 중인 행의 임시 데이터 */
interface EditingRowData {
  createdAt: Dayjs;
  memo: string | null;
}

/**
 * 히스토리 관리 컴포넌트
 * - 히스토리 목록 표시 (테이블 인라인 편집)
 * - 수동 히스토리 추가: 추가 버튼 클릭 시 새 행이 테이블에 생성되고 인라인 편집 모드로 진입
 * - 수동 생성된 항목만 편집/삭제 가능
 */
const LiabilityHistoryManager: FC<LiabilityHistoryManagerProps> = ({ liability, currentUserId }) => {
  // 편집 중인 행의 인덱스 (null: 편집 중 아님, -1: 새로 추가 중인 행)
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // 편집 중인 행의 임시 데이터
  const [editingData, setEditingData] = useState<EditingRowData | null>(null);

  const { users } = useSWRUsers([['role', 'in', ['manager', 'admin']]]);
  const history = useMemo(() => liability.history ?? [], [liability.history]);

  // 사용자 ID로 이름 조회
  const getUserName = useCallback(
    (userId: string) => {
      const user = users?.find((u) => u._id === userId);
      return user ? formatDisplayName(user) : userId;
    },
    [users],
  );

  /**
   * 새 히스토리 추가 시작 (테이블에 새 행 생성)
   */
  const handleAddHistory = useCallback(() => {
    setEditingIndex(-1); // -1은 새로 추가 중인 행을 의미
    setEditingData({
      createdAt: dayjs(),
      memo: null,
    });
  }, []);

  /**
   * 히스토리 편집 시작 (해당 행을 인라인 편집 모드로 전환)
   */
  const handleEditHistory = useCallback((index: number, entry: IssueLiabilityHistoryEntry) => {
    setEditingIndex(index);
    setEditingData({
      createdAt: dayjs(entry.createdAt),
      memo: entry.memo,
    });
  }, []);

  /**
   * 편집 취소
   */
  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditingData(null);
  }, []);

  /**
   * 히스토리 저장 (추가 또는 편집)
   */
  const handleSaveHistory = useCallback(async () => {
    if (!editingData) return;

    try {
      const newHistory = [...history];

      if (editingIndex === -1) {
        // 추가 모드: 새 히스토리 항목 추가
        const newEntry: IssueLiabilityHistoryEntry = {
          createdAt: formatDate(editingData.createdAt.toDate(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
          memo: editingData.memo || null,
          createdBy: currentUserId,
          isManual: true,
        };
        newHistory.push(newEntry);
      } else if (editingIndex !== null) {
        // 편집 모드: 기존 항목 업데이트
        const targetEntry = newHistory[editingIndex];
        if (!targetEntry.isManual) {
          message.error('자동 생성된 히스토리는 편집할 수 없습니다.');
          return;
        }
        newHistory[editingIndex] = {
          ...targetEntry,
          createdAt: formatDate(editingData.createdAt.toDate(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
          memo: editingData.memo || null,
        };
      }

      await updateIssueLiability(liability._id, { history: newHistory });
      message.success(editingIndex === -1 ? '히스토리가 추가되었습니다.' : '히스토리가 수정되었습니다.');
      handleCancelEdit();
    } catch (error) {
      console.error('Save history failed:', error);
      message.error('히스토리 저장에 실패했습니다.');
    }
  }, [editingData, editingIndex, history, liability._id, currentUserId, handleCancelEdit]);

  /**
   * 히스토리 삭제
   */
  const handleDeleteHistory = useCallback(
    async (index: number) => {
      const targetEntry = history[index];
      if (!targetEntry.isManual) {
        message.error('자동 생성된 히스토리는 삭제할 수 없습니다.');
        return;
      }

      try {
        const newHistory = history.filter((_, i) => i !== index);
        await updateIssueLiability(liability._id, { history: newHistory });
        message.success('히스토리가 삭제되었습니다.');
      } catch (error) {
        console.error('Delete history failed:', error);
        message.error('히스토리 삭제에 실패했습니다.');
      }
    },
    [history, liability._id],
  );

  /** 편집 중인 행인지 확인 */
  const isEditingRow = useCallback((index: number) => editingIndex === index, [editingIndex]);

  const historyColumns: ColumnsType<IssueLiabilityHistoryEntry & { index: number }> = [
    {
      title: '시간',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (createdAt: string, record) => {
        if (isEditingRow(record.index)) {
          return (
            <DatePicker
              showTime
              format='YYYY-MM-DD HH:mm'
              value={editingData?.createdAt}
              onChange={(value) => setEditingData((prev) => (prev ? { ...prev, createdAt: value ?? dayjs() } : null))}
              style={{ width: '100%' }}
              size='small'
            />
          );
        }
        return formatDate(new Date(createdAt), 'yyyy-MM-dd HH:mm');
      },
    },
    {
      title: '메모',
      dataIndex: 'memo',
      key: 'memo',
      ellipsis: true,
      render: (memo: string | null, record) => {
        if (isEditingRow(record.index)) {
          return (
            <Input
              value={editingData?.memo ?? ''}
              onChange={(e) => setEditingData((prev) => (prev ? { ...prev, memo: e.target.value || null } : null))}
              placeholder='메모 (선택)'
              size='small'
            />
          );
        }
        return memo || '-';
      },
    },
    {
      title: '생성자',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 100,
      ellipsis: true,
      render: (createdBy: string, record) => {
        // 새로 추가 중인 행인 경우 현재 사용자 이름 표시
        if (record.index === -1) {
          return getUserName(currentUserId);
        }
        return getUserName(createdBy);
      },
    },
    {
      title: '유형',
      dataIndex: 'isManual',
      key: 'isManual',
      width: 80,
      render: (isManual: boolean, record) => {
        // 새로 추가 중인 행은 항상 수동
        if (record.index === -1) {
          return <Tag color='blue'>수동</Tag>;
        }
        return <Tag color={isManual ? 'blue' : 'default'}>{isManual ? '수동' : '자동'}</Tag>;
      },
    },
    {
      title: '작업',
      key: 'actions',
      width: 100,
      render: (_, record) => {
        // 편집 중인 행인 경우 저장/취소 버튼 표시
        if (isEditingRow(record.index)) {
          return (
            <Space size={4}>
              <Tooltip title='저장'>
                <Button type='text' size='small' icon={<CheckOutlined />} onClick={handleSaveHistory} />
              </Tooltip>
              <Tooltip title='취소'>
                <Button type='text' size='small' icon={<CloseOutlined />} onClick={handleCancelEdit} />
              </Tooltip>
            </Space>
          );
        }

        // 수동 생성된 항목만 편집/삭제 가능
        if (record.isManual) {
          return (
            <Space size={4}>
              <Tooltip title='편집'>
                <Button
                  type='text'
                  size='small'
                  icon={<EditOutlined />}
                  onClick={() => handleEditHistory(record.index, record)}
                  disabled={editingIndex !== null}
                />
              </Tooltip>
              <Popconfirm
                title='삭제하시겠습니까?'
                onConfirm={() => handleDeleteHistory(record.index)}
                okText='삭제'
                cancelText='취소'
              >
                <Tooltip title='삭제'>
                  <Button type='text' size='small' danger icon={<DeleteOutlined />} disabled={editingIndex !== null} />
                </Tooltip>
              </Popconfirm>
            </Space>
          );
        }

        return <span style={{ color: '#8c8c8c', fontSize: 12 }}>-</span>;
      },
    },
  ];

  // 데이터소스: 새로 추가 중인 행이 있으면 맨 위에 표시
  const dataSource = useMemo(() => {
    const baseData = history.map((entry, index) => ({ ...entry, index }));

    // 새로 추가 중인 행 (editingIndex === -1)
    if (editingIndex === -1 && editingData) {
      const newRow: IssueLiabilityHistoryEntry & { index: number } = {
        createdAt: editingData.createdAt.toISOString(),
        memo: editingData.memo,
        createdBy: currentUserId,
        isManual: true,
        index: -1,
      };
      return [newRow, ...baseData];
    }

    return baseData;
  }, [history, editingIndex, editingData, currentUserId]);

  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>변경 히스토리</span>
        <Button
          type='link'
          size='small'
          icon={<PlusOutlined />}
          onClick={handleAddHistory}
          disabled={editingIndex !== null}
        >
          수동 히스토리 추가
        </Button>
      </div>

      {/* 히스토리 테이블 (인라인 편집) */}
      <Table
        dataSource={dataSource}
        columns={historyColumns}
        rowKey='index'
        pagination={false}
        size='small'
        locale={{ emptyText: '히스토리가 없습니다.' }}
      />
    </div>
  );
};

export default LiabilityHistoryManager;
